import NiceModal from '@ebay/nice-modal-react';

import React, { useEffect, useId, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Popper from 'components/global/Popper';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { FORM } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faArrowDownToLine, faCloudArrowUp, faEye, faTrashCan, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck, faCircleNotch, faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';

import Button from './Button';
import { ImageModal } from './ImageViewer';
import * as S from './styled/AdvanceFileInput.styled';
import { InputMessageContainer } from './styled/InputCommon.styled';

/**
 * @author @muhammedburakak
 * @description File input component.
 * @param {Object} props - props of the component.
 * @param {string} [props.name] - name of the input field.
 * @param {string} [props.uploadURL] - uploadURL of the input field.
 * @param {string} [props.additionalParams] - additionalParams of which will be sent to the server.
 * @param {Object} [props.value] - value prop form the form
 * @param {Object} [props.setValue] - setValue prop form the form
 * @param {boolean} [props.disabled = false] - If true, the button will be disabled.
 * @param {Object} [props.acceptedFileType] - Accepted file type will only be uploaded. (.xls, .xlsx, .csv, .png, .jpg, .pdf)
 * @param {Object} [props.btnStyle] - Custom css styles for progress bar by inline jsx styling. style={{backgroundColor: "lightblue"}}
 * @param {Object} [props.errorMessage] - Error message of the input field.
 * @param {string} [props.title] - Title of the input field.
 * @param {string} [props.maxFileSize] - Max file size is to limit the file size with MB
 * @param {string} [props.fileLimit] - File limit is to limit the file count
 * @param {*} [props.children] - The content of the component. If it's empty only label and icon will be shown.
 * @returns {object} - Button object with the given props.
 * @example

 *
 * <FileInput
 * 	acceptedFileType=".png,.jpg,.jpeg,"
 *  setValue={setValue}
 * 	value={value}
 * 	fileLimit={1}
 * 	maxFileSize={1}
 * 	disabled
 * />
 */

const MIMEType = {
	'.xls': 'application/vnd.ms-excel',
	'.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'.csv': 'text/csv',
	'.png': 'image/png',
	'.jpg': 'image/jpeg',
	'.jpeg': 'image/jpeg',
	'.pdf': 'application/pdf',
	'.ico': 'image/vnd.microsoft.icon'
};

const AdvanceFileInput = ({
	additionalParams = {},
	uploadUrl = null,
	acceptedFileType = '*',
	maxFileSize,
	fileLimit = 1,
	disabled = false,
	viewMode = false,
	value,
	setValue,
	title,
	btnStyle = {},
	name,
	errorMessage: errorMessageProp,
	height = '20rem',
	showDragDropMessage = true,
	...moreProps
}) => {
	const { showError } = useAlert();
	const axiosPrivate = useAxiosPrivate();
	const { t } = useTranslation();
	const ref = useRef(null);

	const variant = ['.png', '.jpg', '.jpeg'].some((val) => acceptedFileType.includes(val)) ? 'image' : 'file';
	let errorMessage = null;

	const [downloadButtonRef, setDownloadButtonRef] = useState(null);
	const [uploadedPaths, setUploadedPaths] = useState([]);
	const [uploadedFiles, setUploadedFiles] = useState([]);
	const [uploadingFileName, setUploadingFileName] = useState('');
	const [uploadingPaths, setUploadingPaths] = useState([]);
	const [internalErrorMessages, setInternalErrorMessages] = useState([]);
	const [isLoadingInitial, setIsLoadingInitial] = useState(true);

	const [isLoading, setIsLoading] = useState(false);

	const formMethods = useFormContext();

	errorMessage = formMethods?.getFieldState?.(name, formMethods?.formState)?.error?.message ?? errorMessageProp;

	useEffect(() => {
		if (value?.path !== null && variant === 'file') {
			const uploadedPaths = Array.isArray(value) ? value.map((file) => file.path) : [value.path];
			const uploadedFiles = Array.isArray(value)
				? value.map((file) => ({ name: file.name }))
				: [{ name: value.name }];
			setUploadedPaths(uploadedPaths);
			setUploadedFiles(uploadedFiles);
		} else if (value !== null && variant === 'image') {
			setUploadedPaths(Array.isArray(value) ? value : [value]);
			setUploadedFiles(Array.isArray(value) ? value : [value]);
		}
		setIsLoadingInitial(false);
	}, [value, variant]);

	const onRemoveFile = (index) => {
		if (disabled) return;

		const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
		setUploadedFiles(updatedFiles);

		const updatedPaths = uploadedPaths.filter((_, i) => i !== index);
		setUploadedPaths(updatedPaths);

		const updatedInternalErrorMessages = internalErrorMessages.filter((_, i) => i !== index);
		setInternalErrorMessages(updatedInternalErrorMessages);

		const newValue =
			variant === 'file'
				? updatedPaths.length > 0
					? updatedPaths
					: { name: null, path: null }
				: updatedPaths.length > 0
					? updatedPaths
					: null;
		setValue(newValue);
	};

	const onFileChange = async (event) => {
		if (disabled || !event?.target.files) return;

		if (fileLimit === uploadedFiles.length) {
			showError({
				header: 'errorMsg.fileLimitHeader',
				message: 'errorMsg.fileLimitMessage'
			});

			return;
		}

		const files = [...event.target.files];

		const isAcceptedFileType = (file) => {
			const acceptedFileTypeArray = acceptedFileType?.split(',');

			if (!file || !file.name) {
				return false;
			}

			const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
			const fileType = file.type.toLowerCase();

			return (
				acceptedFileType === '*' ||
				acceptedFileTypeArray.some((type) => {
					const trimmedType = type.trim().toLowerCase();
					return MIMEType[trimmedType] === fileType || fileExtension === trimmedType;
				})
			);
		};

		const uploadingFiles = [
			...uploadedFiles,
			...files.filter((file) => file.size / (1024 * 1024) <= maxFileSize && isAcceptedFileType(file))
		];

		const uploadingPaths = [...uploadedPaths];

		const processFile = async (file) => {
			const calculatedFileSize = file.size / (1024 * 1024);

			if (calculatedFileSize > maxFileSize) {
				showError({
					header: 'errorMsg.maxFileSizeHeader',
					message: 'errorMsg.maxFileSizeMessage'
				});
				setInternalErrorMessages(['errorMsg.maxFileSizeMessage']);
				setIsLoading(false);
				return;
			}

			try {
				await setUploadedFilesUpdate(uploadingFiles);
				setUploadingPaths([...uploadingPaths, true]);

				const formData = new FormData();

				uploadingFiles.forEach((file) => {
					formData.append('file', file);
					formData.append('name', file.name);
					formData.append('type', 'import');

					Object.keys(additionalParams)?.forEach((key) => {
						formData.append(key, additionalParams[key]);
					});
				});

				const config = {
					method: 'post',
					url: uploadUrl ?? FORM.UPLOAD_FILE,
					data: formData,
					headers: {
						'Content-Type': 'multipart/form-data'
					},
					transformRequest: () => formData
				};

				const response = await axiosPrivate.request(config);

				uploadingPaths.push(response.data.path);

				await setUpdatedValuePathsUpdate(uploadingPaths, uploadingFiles);

				setUploadingPaths([...uploadingPaths, false]);
			} catch (error) {
				const updatedMessages = [...internalErrorMessages];
				updatedMessages.push('errorMsg.serverErrorMessage');
				setInternalErrorMessages(updatedMessages);

				AppLogger.error(error);
				if (error.response === 500) {
					showError({
						header: 'errorMsg.serverErrorHeader',
						message: 'errorMsg.serverErrorMessage'
					});
				} else {
					showError();
				}
			} finally {
				setIsLoading(false);
			}
		};

		const setUploadedFilesUpdate = async (files) => {
			setUploadedFiles(files);
		};

		const setUpdatedValuePathsUpdate = async (paths, files) => {
			setUploadedPaths(paths);

			if (variant === 'file') {
				await setValue(
					fileLimit === 1
						? {
								name: files[0].name,
								path: paths[0]
							}
						: paths.map((path, index) => ({
								name: files[index].name,
								path
							}))
				);
			} else if (variant === 'image') {
				await setValue(fileLimit === 1 ? paths[0] : paths);
			}
		};

		const processFilesSequentially = async () => {
			if (fileLimit >= uploadingFiles.length) {
				setInternalErrorMessages([]);

				for (const file of files) {
					if (!isAcceptedFileType(file)) {
						showError({
							header: 'errorMsg.fileTypeHeader',
							message: 'errorMsg.fileTypeMessage'
						});
						setInternalErrorMessages(['errorMsg.fileTypeMessage']);
					} else {
						setUploadingFileName(file.name);
						setIsLoading(true);
						await processFile(file);
					}
				}
			} else {
				showError({
					header: 'errorMsg.fileLimitHeader',
					message: 'errorMsg.fileLimitMessage'
				});
				setInternalErrorMessages(['errorMsg.fileLimitMessage']);
			}
		};

		await processFilesSequentially();
	};

	useEffect(() => {
		const handleDragOver = (e) => {
			e.preventDefault();
			e.stopPropagation();
		};

		const handleDrop = (e) => {
			e.preventDefault();
			e.stopPropagation();
			const { files } = e.dataTransfer;
			if (files.length > 0 && !disabled) {
				onFileChange({ target: { files } });
			}
		};

		ref.current.addEventListener('dragover', handleDragOver);
		ref.current.addEventListener('drop', handleDrop);

		return () => {
			ref.current?.removeEventListener('dragover', handleDragOver);
			ref.current?.removeEventListener('drop', handleDrop);
		};
	}, [onFileChange]);

	return (
		<div ref={ref} {...moreProps}>
			{!isLoadingInitial ? (
				uploadedFiles.length > 0 ? (
					isLoading ? (
						<>
							<UploadContainer
								errorMessage={
									errorMessage || (internalErrorMessages.length > 0 ? internalErrorMessages[0] : '')
								}
								acceptedFileType={acceptedFileType}
								uploadedPaths={uploadedPaths}
								uploadingFileName={uploadingFileName}
								disabled={disabled}
								focus={isLoading}
								onFileChange={onFileChange}
								fileLimit={fileLimit}
								maxFileSize={maxFileSize}
								btnStyle={btnStyle}
								title={title}
								height={height}
								showDragDropMessage={showDragDropMessage}
							/>

							{variant === 'file' &&
								uploadedFiles.map((file, index) => (
									<S.FlexRow key={index} className={'mt-4'} errorMessage={errorMessage}>
										<div>
											<Typography
												variant="p3"
												fontWeight={500}
												noWrap
												maxlines={1}
												style={{ width: '70rem' }}>
												<a href={uploadedPaths[index]} style={{ color: 'var(--blue)' }}>
													{t('fileInput.fileName', { index: index + 1, fileName: file.name })}
												</a>
											</Typography>

											{uploadedPaths[index] && (
												<StatusContainer
													backgroundColor="var(--green-10)"
													text="fileInput.uploaded"
													borderColor="var(--green-70)"
												/>
											)}

											{uploadingPaths[index] === true && !uploadedPaths[index] && (
												<StatusContainer
													backgroundColor="var(--blue-10)"
													text="fileInput.uploading"
													borderColor="var(--blue-70)"
												/>
											)}

											{!uploadedPaths[index] && !uploadingPaths[index] && (
												<StatusContainer
													backgroundColor="var(--yellow-10)"
													text="fileInput.inQueue"
													borderColor="var(--yellow-70)"
												/>
											)}
										</div>

										<Button
											variant="borderless"
											icon={faXmark}
											iconSize="medium"
											disabled={!uploadedPaths[index]}
											onClick={() => onRemoveFile(index)}
											iconButton
										/>
									</S.FlexRow>
								))}
						</>
					) : (
						<>
							{!viewMode && fileLimit !== uploadedFiles.length && (
								<UploadContainer
									errorMessage={
										errorMessage ||
										(internalErrorMessages.length > 0 ? internalErrorMessages[0] : '')
									}
									acceptedFileType={acceptedFileType}
									disabled={
										disabled ||
										(errorMessage && internalErrorMessages.length > 0) ||
										(fileLimit === uploadedFiles.length &&
											!errorMessage &&
											internalErrorMessages.length === 0)
									}
									onFileChange={onFileChange}
									successState={fileLimit === uploadedFiles.length}
									fileLimit={fileLimit}
									maxFileSize={maxFileSize}
									btnStyle={btnStyle}
									title={title}
									height={height}
									showDragDropMessage={showDragDropMessage}
								/>
							)}

							{uploadedFiles.map((file, index) => (
								<React.Fragment key={index}>
									{variant === 'image' && (
										<ImageContainer
											className={!viewMode ? 'mt-4' : 'mb-2'}
											imageUrl={uploadedPaths[index]}
											viewMode={viewMode || disabled}
											height={height}
											removeAction={() => onRemoveFile(index)}
										/>
									)}

									{variant === 'file' && (
										<S.FlexRow
											className={
												!viewMode && fileLimit !== uploadedFiles.length ? 'mt-4' : 'mb-2'
											}
											errorMessage={errorMessage}>
											<div>
												<Button
													variant="borderless"
													ref={setDownloadButtonRef}
													size="x-small"
													onClick={() =>
														!viewMode && window.open(uploadedPaths[index], '_self')
													}>
													<Typography
														variant="p3"
														fontWeight={500}
														noWrap
														maxlines={1}
														style={{
															textDecoration: 'underline',
															textAlign: 'start',
															width: '20rem'
														}}
														color="var(--blue)">
														{index + 1}. {file.name}
													</Typography>
												</Button>

												{downloadButtonRef && !viewMode && (
													<Popper
														refElement={downloadButtonRef}
														placement="right"
														offsetX={0}
														offsetY={-5}
														hoverMode>
														{() => (
															<Button
																variant="borderless"
																icon={faArrowDownToLine}
																iconSize="small"
																disabled={disabled}
																onClick={() =>
																	window.open(uploadedPaths[index], '_self')
																}
																iconButton
															/>
														)}
													</Popper>
												)}

												{!viewMode && (
													<StatusContainer
														backgroundColor="var(--green-10)"
														text="fileInput.uploaded"
														borderColor="var(--green-70)"
													/>
												)}
											</div>

											{!viewMode && (
												<Button
													variant="borderless"
													icon={faXmark}
													iconSize="medium"
													disabled={disabled}
													onClick={() => onRemoveFile(index)}
													iconButton
												/>
											)}
										</S.FlexRow>
									)}
								</React.Fragment>
							))}
						</>
					)
				) : (
					!viewMode && (
						<UploadContainer
							errorMessage={
								errorMessage || (internalErrorMessages.length > 0 ? internalErrorMessages[0] : '')
							}
							acceptedFileType={acceptedFileType}
							disabled={disabled || viewMode}
							focus={isLoading}
							successState={fileLimit === uploadedFiles.length}
							onFileChange={onFileChange}
							fileLimit={fileLimit}
							maxFileSize={maxFileSize}
							btnStyle={btnStyle}
							variant={variant}
							title={title}
							height={height}
							showDragDropMessage={showDragDropMessage}
						/>
					)
				)
			) : (
				<Skeleton width="100%" height="5rem" />
			)}
		</div>
	);
};

export default AdvanceFileInput;

const UploadContainer = ({
	errorMessage,
	acceptedFileType,
	disabled,
	focus,
	onFileChange,
	fileLimit,
	maxFileSize,
	btnStyle,
	variant,
	successState,
	uploadingFileName,
	title,
	height,
	showDragDropMessage = true
}) => {
	const { t } = useTranslation();

	const inputId = useId();

	return (
		<>
			<S.UploadContainer
				onClick={() => {
					document.getElementById(inputId).click();
				}}
				height={height}
				className={`${errorMessage ? 'error ' : ''} ${disabled ? 'disabled ' : ''} ${focus ? 'focus ' : ''}`}>
				<div style={{ width: '100%' }}>
					{variant === 'image' && title && (
						<Typography variant="p2" className={'mb-4'} fontWeight={500}>
							{title}
						</Typography>
					)}
					{uploadingFileName && (
						<Icon icon={faCircleNotch} className="fa-spin mb-4" fontSize={'5rem'} color="var(--blue)" />
					)}

					{!uploadingFileName && (
						<Icon icon={faCloudArrowUp} className="mb-4" fontSize={'5rem'} color="var(--content-2)" />
					)}

					<S.ButtonContainer className={variant === 'file' ? 'mb-4' : ''} style={{ width: '100%' }}>
						{uploadingFileName ? (
							<Typography
								variant="p3"
								fontWeight={500}
								noWrap
								maxlines={1}
								align="center"
								style={{ width: '90%' }}>
								{uploadingFileName}
							</Typography>
						) : (
							<S.TextContainer>
								{showDragDropMessage && (
									<Typography variant="p3" fontWeight={500}>
										dragAndDropFilesOr
									</Typography>
								)}

								<S.BrowseButton
									component="label"
									variant="text"
									label="browse.fileInput"
									className="file-input-button pt-3"
									disabled={disabled}
									style={btnStyle}
								/>
							</S.TextContainer>
						)}
					</S.ButtonContainer>

					{variant === 'file' && !uploadingFileName && (
						<Typography variant="caption" color="var(--content-2)" noWrap>
							{t('supportedFormats.fileInput', {
								fileType:
									acceptedFileType === '*'
										? t('all.fileInput')
										: acceptedFileType?.split(',').join(', ').replace(/\./g, '').toUpperCase(),
								fileLimit,
								maxFileSize
							})}
						</Typography>
					)}
					{uploadingFileName && (
						<Typography variant="caption" color="var(--content-2)" className="my-2" align="center" noWrap>
							pleaseWaitWhileYourFileIsUploading
						</Typography>
					)}
				</div>
				<input
					id={inputId}
					accept={acceptedFileType}
					hidden
					onChange={(e) => {
						onFileChange(e);
					}}
					disabled={disabled}
					type="file"
					multiple={fileLimit > 1}
				/>
			</S.UploadContainer>

			{successState && (
				<InputMessageContainer>
					<Icon icon={faCircleCheck} color="var(--green)" size="x-small" />
					<Typography variant="caption" fontWeight="500" color="var(--green)" noWrap>
						successMsg.fileUploadedSuccessfully
					</Typography>
				</InputMessageContainer>
			)}

			{errorMessage && (
				<InputMessageContainer className="error-message-container">
					<Icon icon={faExclamationCircle} color="var(--red)" size="x-small" />
					<Typography variant="caption" fontWeight="500" color="var(--red)" noWrap>
						{errorMessage}
					</Typography>
				</InputMessageContainer>
			)}
		</>
	);
};

const ImageContainer = ({ imageUrl, removeAction, className, viewMode, height }) => (
	<S.ImageContainer className={className}>
		<S.Miniscreen>
			<Image
				url={imageUrl}
				width="100%"
				height={height ? height : '20rem'}
				alt="image"
				referrerPolicy="no-referrer"
			/>

			<S.DarkContainer>
				<S.IconContainer
					className="mx-4"
					onClick={() => {
						NiceModal.show(ImageModal, { imageUrl });
					}}>
					<Icon icon={faEye} color="var(--white)" size="medium" />
				</S.IconContainer>

				{!viewMode && (
					<S.IconContainer onClick={removeAction}>
						<Icon icon={faTrashCan} color="var(--white)" size="medium" />
					</S.IconContainer>
				)}
			</S.DarkContainer>
		</S.Miniscreen>
	</S.ImageContainer>
);

const StatusContainer = ({ backgroundColor, text, borderColor }) => (
	<S.StatusContainer
		style={{
			backgroundColor,
			border: `1px solid ${borderColor}`
		}}>
		<Typography variant="p3" fontWeight="500" color="var(--content-1)">
			{text}
		</Typography>
	</S.StatusContainer>
);
